import { toWesternDigits } from "@sirdab/foundation";

/**
 * Phone data and parsing for the PhoneInput.
 *
 * A phone number is an IDENTIFIER (numerals rule, foundation AGENTS §5):
 * it never carries Arabic-Indic digits and it never mirrors. Everything
 * here works in Latin digits and hands back Latin digits.
 *
 * The list is DELIBERATELY SHORT. An exhaustive dataset (250 entries plus
 * libphonenumber's metadata) is a hundred kilobytes to make a merchant
 * scroll past Vanuatu on their way to Saudi. These are the countries our
 * merchants and their customers actually dial from; adding one is a data
 * line, not a code change.
 */

export type PhoneCountry = {
  /** ISO 3166-1 alpha-2, uppercase. Drives the flag and the label. */
  readonly code: string;
  /** Country calling code, no plus. */
  readonly dial: string;
  /** Display grouping of the national number, in digits per group. */
  readonly groups: readonly number[];
  /** National subscriber length bounds, inclusive. */
  readonly min: number;
  readonly max: number;
  /** Whether a national number is written with a trunk 0 to strip. */
  readonly trunkZero: boolean;
};

/* Saudi leads, then the GCC in dialling order, then the countries our
   merchants ship to and sell from most. */
export const PHONE_COUNTRIES: readonly PhoneCountry[] = [
  {
    code: "SA",
    dial: "966",
    groups: [2, 3, 4],
    min: 9,
    max: 9,
    trunkZero: true,
  },
  {
    code: "AE",
    dial: "971",
    groups: [2, 3, 4],
    min: 8,
    max: 9,
    trunkZero: true,
  },
  {
    code: "KW",
    dial: "965",
    groups: [4, 4],
    min: 8,
    max: 8,
    trunkZero: false,
  },
  {
    code: "BH",
    dial: "973",
    groups: [4, 4],
    min: 8,
    max: 8,
    trunkZero: false,
  },
  {
    code: "QA",
    dial: "974",
    groups: [4, 4],
    min: 8,
    max: 8,
    trunkZero: false,
  },
  {
    code: "OM",
    dial: "968",
    groups: [4, 4],
    min: 8,
    max: 8,
    trunkZero: false,
  },
  {
    code: "EG",
    dial: "20",
    groups: [3, 3, 4],
    min: 8,
    max: 10,
    trunkZero: true,
  },
  {
    code: "JO",
    dial: "962",
    groups: [2, 3, 4],
    min: 8,
    max: 9,
    trunkZero: true,
  },
  {
    code: "TR",
    dial: "90",
    groups: [3, 3, 4],
    min: 10,
    max: 10,
    trunkZero: true,
  },
  {
    code: "GB",
    dial: "44",
    groups: [4, 6],
    min: 9,
    max: 10,
    trunkZero: true,
  },
  {
    code: "US",
    dial: "1",
    groups: [3, 3, 4],
    min: 10,
    max: 10,
    trunkZero: false,
  },
];

/** Saudi is the default everywhere in the studio [Sirdab]. */
export const DEFAULT_PHONE_COUNTRY = "SA";

const BY_CODE = new Map(PHONE_COUNTRIES.map((c) => [c.code, c]));

export function phoneCountry(code: string): PhoneCountry {
  return BY_CODE.get(code) ?? BY_CODE.get(DEFAULT_PHONE_COUNTRY)!;
}

/**
 * The emoji flag, derived from the ISO code's two letters as regional
 * indicator symbols. No image assets and no icon package: the platform
 * font draws it, and where it cannot (Windows ships no flag glyphs) the
 * dial code beside it is what carries the meaning, so the control still
 * reads. Returns "" for anything that is not two ASCII letters.
 */
export function flagEmoji(code: string): string {
  if (!/^[A-Za-z]{2}$/.test(code)) return "";
  const OFFSET = 0x1f1e6 - "A".charCodeAt(0);
  return String.fromCodePoint(
    ...[...code.toUpperCase()].map((ch) => ch.charCodeAt(0) + OFFSET),
  );
}

/**
 * The country's name in the reader's language, from the runtime's CLDR
 * data. Country names are the one string set a primitive can localise
 * without carrying copy (UI AGENTS: i18n-free primitives), because the
 * platform already ships them in every locale we serve. Falls back to the
 * ISO code on a runtime without DisplayNames.
 */
export function countryLabel(code: string, locale?: string): string {
  try {
    return (
      new Intl.DisplayNames([locale ?? "en"], { type: "region" }).of(code) ??
      code
    );
  } catch {
    return code;
  }
}

/** Longest dial code first, so +971 never matches as +9 then 71. */
const BY_DIAL = [...PHONE_COUNTRIES].sort(
  (a, b) => b.dial.length - a.dial.length,
);

export type ParsedPhone = {
  /** The country the input resolved to. */
  country: string;
  /** National subscriber digits, no trunk 0, no country code. */
  national: string;
};

/**
 * Collapse anything a user can produce into (country, national digits):
 * a pasted `+966 55 178 5151`, a local `0551785151`, an autofilled
 * `966551785151`, Arabic-Indic `٠٥٥١٧٨٥١٥١`, or a half-typed `55`.
 *
 * An explicit international prefix (`+` or `00`) picks the country; bare
 * digits stay in `fallback`, because `1234…` is a London landline in GB
 * and a subscriber number everywhere else, and guessing is worse than
 * honouring the selector the user is looking at.
 */
export function parsePhone(raw: string, fallback: string): ParsedPhone {
  const western = toWesternDigits(raw ?? "");
  const international = /^\s*(?:\+|00)/.test(western);
  const digits = western.replace(/\D/g, "");
  if (digits === "") return { country: fallback, national: "" };

  if (international) {
    const body = digits.replace(/^00/, "");
    const hit = BY_DIAL.find((c) => body.startsWith(c.dial));
    if (hit)
      return {
        country: hit.code,
        national: trim(hit, body.slice(hit.dial.length)),
      };
    return {
      country: fallback,
      national: trim(phoneCountry(fallback), body),
    };
  }

  /* A bare number that opens with the selected country's own dial code
       is an autofill or a copy-paste that lost its plus (`966551785151`),
       but only when what remains could BE a subscriber number: `9665`
       typed digit by digit in KSA is the start of a real 966-5 number. */
  const country = phoneCountry(fallback);
  if (digits.startsWith(country.dial)) {
    const rest = digits.slice(country.dial.length);
    if (rest.length >= country.min) {
      return { country: country.code, national: trim(country, rest) };
    }
  }
  return { country: country.code, national: trim(country, digits) };
}

function trim(country: PhoneCountry, national: string): string {
  const stripped = country.trunkZero ? national.replace(/^0+/, "") : national;
  return stripped.slice(0, country.max);
}

/** Group the national number for display: `55 178 5151` in Saudi. */
export function formatNational(code: string, national: string): string {
  const { groups } = phoneCountry(code);
  const out: string[] = [];
  let rest = national;
  for (const size of groups) {
    if (rest === "") break;
    out.push(rest.slice(0, size));
    rest = rest.slice(size);
  }
  if (rest !== "") out.push(rest);
  return out.join(" ");
}

/**
 * Saudi keeps its exact rule (every mobile is `5XXXXXXXX`) because the
 * whole studio dedupes customers and sends OTPs on it. Everywhere else is
 * a length range on purpose: a fake-precise per-operator regex goes stale
 * the week a regulator opens a new prefix, and rejecting a real number is
 * worse than accepting a typo the SMS gateway will bounce.
 */
export function isValidNational(code: string, national: string): boolean {
  if (!/^\d*$/.test(national)) return false;
  if (code === "SA") return /^5\d{8}$/.test(national);
  const { min, max } = phoneCountry(code);
  return national.length >= min && national.length <= max;
}

/** `+9665XXXXXXXX`. Empty national digits give "", never a bare `+966`. */
export function toE164(code: string, national: string): string {
  return national === "" ? "" : `+${phoneCountry(code).dial}${national}`;
}

/** True when `value` is a complete, dialable E.164 number. */
export function isValidE164(value: string, fallback: string): boolean {
  const { country, national } = parsePhone(value, fallback);
  return isValidNational(country, national);
}
